import { BallBeatSpinner } from "@/components/loaders/BallBeatSpinner";
import MobileAppLinks from "@/components/MobileAppLinks";
import { Button } from "@/components/ui/button";
import { formatCount } from "@/lib/western-digits";
import { HugeiconsIcon } from "@hugeicons/react";
import { Copy01Icon, LinkSquare02Icon } from "@hugeicons-pro/core-stroke-standard";
import { toast } from "sonner";
import { FamilySearchResult, useFamilyDiscoverySearch } from "./useFamilyDiscoverySearch";

function FamilySearchResults({
  families,
  hasError,
  isLoading,
  onSelect,
}: {
  families: FamilySearchResult[];
  hasError: boolean;
  isLoading: boolean;
  onSelect: (family: FamilySearchResult) => void;
}) {
  return (
    <div className="mt-3 overflow-hidden rounded-md border border-gray-200 bg-white">
      {isLoading ? (
        <div className="flex items-center justify-center px-4 py-5">
          <BallBeatSpinner size="sm" />
        </div>
      ) : null}

      {hasError ? <p className="px-4 py-4 text-sm text-gray-500">تعذر البحث الآن. حاول مرة أخرى بعد قليل.</p> : null}

      {!isLoading && !hasError && families.length === 0 ? (
        <p className="px-4 py-4 text-sm text-gray-500">لا توجد عائلة ظاهرة بهذا البحث.</p>
      ) : null}

      {families.map((family) => (
        <button
          key={family.branch_id}
          type="button"
          onClick={() => onSelect(family)}
          className="flex w-full items-center gap-3 border-t border-gray-100 px-4 py-3 text-right transition first:border-t-0 hover:bg-green-50/60"
        >
          {family.family_logo ? (
            <img
              src={family.family_logo}
              alt=""
              className="size-10 shrink-0 rounded-md border border-gray-100 object-cover"
            />
          ) : (
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-green-50 text-base font-bold text-green-800">
              {family.family_name.slice(0, 1)}
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold text-gray-950">{family.family_name}</span>
            <span className="mt-1 block truncate text-sm text-gray-500">
              {family.url || family.short_code}
              {family.nodes_count ? ` · ${formatCount(family.nodes_count)} فرد` : ""}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

export default function FamilyDiscoverySearch() {
  const {
    appDeepLink,
    canSearch,
    families,
    familyCode,
    hasError,
    isLoading,
    query,
    selectedFamily,
    selectFamily,
    updateQuery,
  } = useFamilyDiscoverySearch();

  const copyFamilyCode = async () => {
    if (!familyCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(familyCode);
      toast.success("تم نسخ رمز العائلة");
    } catch {
      toast.error("تعذر نسخ رمز العائلة");
    }
  };

  return (
    <section className="bg-white py-10 md:py-14" dir="rtl">
      <div className="mx-auto max-w-4xl px-4 text-center md:px-8">
        <p className="text-sm font-semibold text-green-700">دخول أفراد العائلة</p>
        <h2 className="mt-3 text-2xl font-bold text-gray-950 md:text-3xl">ابحث عن شجرتك</h2>
        <p className="mx-auto mt-3 max-w-2xl text-base leading-8 text-gray-600">
          اكتب اسم العائلة أو رمزها لفتحها في تطبيق أوراق.
        </p>

        <div className="mx-auto mt-6 max-w-2xl text-right">
          <label htmlFor="landing-family-search" className="sr-only">
            البحث عن عائلة
          </label>
          <input
            id="landing-family-search"
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
            placeholder="اسم العائلة أو رمزها"
            className="h-12 w-full rounded-md border border-gray-200 bg-white px-4 text-base text-gray-900 transition outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
          />

          {canSearch && !selectedFamily ? (
            <FamilySearchResults
              families={families}
              hasError={hasError}
              isLoading={isLoading}
              onSelect={selectFamily}
            />
          ) : null}

          {selectedFamily ? (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-bold text-gray-950">{selectedFamily.family_name}</h3>
                  <p className="mt-1 font-mono text-sm text-gray-600">{familyCode || "---"}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {appDeepLink ? (
                    <Button asChild className="h-10 gap-2 rounded-md bg-green-700 text-white hover:bg-green-800">
                      <a href={appDeepLink}>
                        <HugeiconsIcon icon={LinkSquare02Icon} strokeWidth={2} className="size-4" />
                        فتح في التطبيق
                      </a>
                    </Button>
                  ) : null}
                  <Button variant="outline" className="h-10 gap-2 rounded-md" onClick={copyFamilyCode}>
                    <HugeiconsIcon icon={Copy01Icon} strokeWidth={2} className="size-4" />
                    نسخ الرمز
                  </Button>
                </div>
              </div>

              <MobileAppLinks className="mt-4 justify-start" />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
